import { SOCIAL_AWARD_META, type SocialAward } from '../../domain/awards/types';
import { formatMonthKey } from '../../domain/leaderboard/types';
import './Awards.css';

/** Effort awards get the green; the headline title gets the accent. */
function modifier(type: SocialAward['type']): string {
  if (type === 'champion') return 'award--headline';
  if (type === 'most_improved' || type === 'volume') return 'award--effort';
  return '';
}

function formatValue(award: SocialAward): string {
  const signed = award.type === 'most_improved' && award.value > 0 ? '+' : '';
  return `${signed}${award.value.toLocaleString()}`;
}

export function AwardRow({ award }: { award: SocialAward }) {
  const meta = SOCIAL_AWARD_META[award.type];
  return (
    <li className={`award ${modifier(award.type)}`}>
      <span className="award__medal" aria-hidden="true">
        {meta.icon}
      </span>
      <span className="award__body">
        <span className="award__name">{meta.label}</span>
        <span className="award__winner">{award.displayName}</span>
        {award.detail && <span className="award__detail">{award.detail}</span>}
      </span>
      <span className="award__value">
        {formatValue(award)}
        <small>{award.unit === 'points' ? 'points' : 'lb'}</small>
      </span>
    </li>
  );
}

export interface AwardsViewProps {
  monthKey: string;
  /** Undefined while loading; empty means the month is over but nobody lifted. */
  awards: SocialAward[] | undefined;
  isCurrentMonth: boolean;
  isLoading: boolean;
  /** Jumps to the most recent finished month from the in-progress state. */
  onGoToLastFinished?: () => void;
  lastFinishedMonthKey?: string;
}

export function AwardsView({
  awards,
  isCurrentMonth,
  isLoading,
  onGoToLastFinished,
  lastFinishedMonthKey,
}: AwardsViewProps) {
  // The current month has no awards on purpose - they are results, and it is
  // not over. Saying so beats an empty list that looks broken.
  if (isCurrentMonth) {
    return (
      <div className="awards-progress">
        <div className="awards-progress__icon" aria-hidden="true">
          🏋️
        </div>
        <p className="awards-progress__title">SWOLing in progress…</p>
        <p className="awards-progress__sub">
          This month&rsquo;s awards are handed out once the month is done.
        </p>
        {onGoToLastFinished && lastFinishedMonthKey && (
          <button type="button" className="awards-progress__go" onClick={onGoToLastFinished}>
            ‹ See {formatMonthKey(lastFinishedMonthKey)} results
          </button>
        )}
      </div>
    );
  }

  if (isLoading) return <div className="leaderboard__loading">Loading...</div>;

  if (!awards || awards.length === 0) {
    return <div className="leaderboard__empty">No lifts were logged this month.</div>;
  }

  return (
    <ul className="awards">
      {awards.map((award) => (
        <AwardRow key={award.type} award={award} />
      ))}
    </ul>
  );
}
